import { Injectable } from '@angular/core';
import {
    calculateCrossedPriceBasedOnPriceAndCrossedPercent,
    canOccupancySelectionFitInRoom,
    processPrice,
} from './hotel-booking.utils';
import { RoomWithCheapestMealPlan } from './hotel-booking.model';
import { OccupancySelection } from '@app/components/search-occupancy/search-occupancy.model';
import { AccomInventory, AgeGroup, InventoryDto } from '@app/model/deal.model';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { CalendarDayInfo } from '@app/controls/calendar/calendar.component';
import {
    createDateFromStringReverse,
    createDateString,
    getDayDifference,
    isNgbDateStructAfterToday,
} from '@app/controls/calendar/calendar.utils';
import * as _ from 'lodash';
import { compileOccupancyQueryString } from '@app/components/search-occupancy/search-occupancy.utils';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class HotelBookingStateService {
    inventoryGroupedByDate: Map<string, AccomInventory[]>;
    constructor(private router: Router) {}

    addDatesAndOccupancyToQueryString(
        fromDate: NgbDate,
        toDate: NgbDate,
        occupancy: OccupancySelection
    ): void {
        const queryParams = {
            fromDate: createDateString(fromDate),
            toDate: createDateString(toDate),
            occupancy: compileOccupancyQueryString(occupancy),
        };
        const url = this.router.url.split('?')[0];
        const newQueryString = new URLSearchParams(queryParams).toString();
        window.history.replaceState({}, '', `${url}?${newQueryString}`);
    }

    isRoomOptionDisabledAndReason(
        room: RoomWithCheapestMealPlan,
        roomIdsWithAvailability: number[],
        occupancy: OccupancySelection,
        ageGroups: AgeGroup[],
        occupancyString: string
    ): { isDisabled: boolean; reason: string } {
        // does the room not fit occupancy?
        const canFit = canOccupancySelectionFitInRoom(
            occupancy,
            room.occupancyLimits,
            ageGroups
        );

        if (!canFit) {
            return {
                isDisabled: true,
                reason: 'This room does not hold ' + occupancyString,
            };
        }

        if (!roomIdsWithAvailability.includes(room.roomId)) {
            return {
                isDisabled: true,
                reason: 'This room has no availability for the dates selected',
            };
        }

        return {
            isDisabled: false,
            reason: '',
        };
    }

    computePriceBasedOnRoomAndMealPlanSelection(
        rawInventory: InventoryDto[],
        fromDate: NgbDate,
        toDate: NgbDate,
        selectedRoomId: number,
        crossedPercent: number
    ): {
        sellingPrice: number;
        crossedPrice: number;
        crossedPercent: number;
    } {
        const inventoryForSelectedTimeframe = rawInventory.filter(
            (inventory) => {
                const date = createDateFromStringReverse(inventory.date);
                return (
                    inventory.roomId === selectedRoomId &&
                    (fromDate.equals(date) ||
                        (fromDate.before(date) && toDate.after(date)))
                );
            }
        );

        const sellingPrice = inventoryForSelectedTimeframe.reduce(
            (acc, inventory) => {
                return acc + inventory.sellingPrice;
            },
            0
        );

        const crossedPrice = calculateCrossedPriceBasedOnPriceAndCrossedPercent(
            sellingPrice,
            crossedPercent
        );

        return {
            sellingPrice,
            crossedPrice,
            crossedPercent,
        };
    }

    computeAdditionalCalendarDayInfo(
        inventory: AccomInventory[],
        roomsWithCheapestMealPlans: RoomWithCheapestMealPlan[]
    ): Map<string, CalendarDayInfo> {
        this.inventoryGroupedByDate = this.groupInventoryByDate(inventory);
        const inventoryWithCheapestDates = Array.from(
            this.inventoryGroupedByDate.values()
        ).map((inventoryForDate) => {
            const cheapestInventory = _.minBy(inventoryForDate, 'sellingPrice');
            return {
                date: cheapestInventory.date,
                dateAsString: cheapestInventory.dateAsString,
                sellingPrice: cheapestInventory.sellingPrice,
                minLengthOfStay: cheapestInventory.minLengthOfStay,
                roomName: this.getRoomNameFromRoomId(
                    cheapestInventory.roomId,
                    roomsWithCheapestMealPlans
                ),
                roomId: cheapestInventory.roomId,
            };
        });

        return new Map(
            inventoryWithCheapestDates
                .filter((item) => isNgbDateStructAfterToday(item.date))
                .map((pricingByDate) => [
                    pricingByDate.dateAsString,
                    {
                        date: pricingByDate.date,
                        tooltipContent: processPrice(
                            pricingByDate.sellingPrice
                        ),
                        minLengthOfStay: pricingByDate.minLengthOfStay,
                        roomIds: this.inventoryGroupedByDate
                            .get(pricingByDate.dateAsString)
                            .map(
                                (inventory: AccomInventory) => inventory.roomId
                            ),
                        roomName: pricingByDate.roomName,
                    },
                ])
        );
    }

    getCheapestRoomIdForSelectedTimeframe(
        fromDate: NgbDate,
        toDate: NgbDate
    ): number {
        const {
            roomIds: roomsAvailableForAllDates,
            inventory: inventoryForSelectedTimeframe,
        } = this.getRoomsAndInventoryAvailableForAllDates(fromDate, toDate);
        const cheapestRoomAvailableForAllDates = _.minBy(
            roomsAvailableForAllDates,
            (roomId) => {
                return _.min(
                    inventoryForSelectedTimeframe.map((inventoryForDate) => {
                        return _.minBy(inventoryForDate, 'sellingPrice')
                            .sellingPrice;
                    })
                );
            }
        );

        return cheapestRoomAvailableForAllDates;
    }

    private getRoomNameFromRoomId(
        roomId: number,
        roomsWithCheapestMealPlans: RoomWithCheapestMealPlan[]
    ): string {
        return roomsWithCheapestMealPlans.find((room) => room.roomId === roomId)
            .roomName;
    }

    private groupInventoryByDate(
        inventory: AccomInventory[]
    ): Map<string, AccomInventory[]> {
        return inventory.reduce((acc, item) => {
            if (!acc.has(item.dateAsString)) {
                acc.set(item.dateAsString, []);
            }
            acc.get(item.dateAsString).push(item);
            return acc;
        }, new Map());
    }

    getRoomsAndInventoryAvailableForAllDates(
        fromDate: NgbDate,
        toDate: NgbDate
    ): { roomIds: number[]; inventory: AccomInventory[][] } {
        if (!this.inventoryGroupedByDate) {
            return {
                roomIds: [],
                inventory: [],
            };
        }

        const inventoryForSelectedTimeframe = Array.from(
            this.inventoryGroupedByDate?.values()
        ).filter((inventoryForDate) => {
            const date = inventoryForDate[0].date;
            return (
                fromDate.equals(date) ||
                (fromDate.before(date) && toDate.after(date))
            );
        });

        const roomIds = _.intersection(
            ...inventoryForSelectedTimeframe.map((inventoryForDate) =>
                inventoryForDate.map((inventory) => inventory.roomId)
            )
        );

        const isInventoryForWholeTimeframe =
            this.doesInventoryCoverAllTimeFrame(
                fromDate,
                toDate,
                inventoryForSelectedTimeframe
            );

        const doesTimeframeCoverMlosOfFromDate =
            this.doesTimeframeCoverMlosOfFromDate(
                fromDate,
                toDate,
                inventoryForSelectedTimeframe
            );

        return {
            roomIds:
                isInventoryForWholeTimeframe && doesTimeframeCoverMlosOfFromDate
                    ? roomIds
                    : [],
            inventory: inventoryForSelectedTimeframe,
        };
    }

    private doesInventoryCoverAllTimeFrame(
        fromDate: NgbDate,
        toDate: NgbDate,
        inventory: AccomInventory[][]
    ) {
        const nrOfDaysInRange = getDayDifference(fromDate, toDate);
        const nrOfDaysWithInventory = inventory.length;
        return nrOfDaysInRange === nrOfDaysWithInventory;
    }

    private doesTimeframeCoverMlosOfFromDate(
        fromDate: NgbDate,
        toDate: NgbDate,
        inventory: AccomInventory[][]
    ) {
        const nrOfDaysInRange = getDayDifference(fromDate, toDate);
        const fromDateItem = inventory.find((inventoryForDate) => {
            const date = inventoryForDate[0]?.date;
            return fromDate?.equals(date);
        });
        const mlosOfFromDate = fromDateItem?.[0]?.minLengthOfStay;
        return nrOfDaysInRange >= mlosOfFromDate;
    }
}
